import { createContext, useContext, useEffect, useState } from "react";
import { HTTPClient } from "../HTTPClient";
import type { Place } from "../types/Place";
import { useAuth } from "./AuthProvider";
import { isAdmin } from "../utils/helpers";
import { useSnackbar } from "./ToastProvider";

export type PlaceProviderContextProps = {
  isLoading: boolean;

  places: Place[];
  selectedPlace: Place | undefined;

  changePlace: (placeId: string) => void;
  createPlace: (place: Omit<Place, "id">) => Promise<Place>;
  refreshPlaces: () => Promise<void>;
};

const PlaceContext = createContext<PlaceProviderContextProps | null>(null);

const PlaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [places, setPlaces] = useState<Place[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<Place>();
  const { searchParams } = new URL(window.location.href);
  const { defaultError } = useSnackbar();
  const storedPlaceId = localStorage.getItem("tgc-selectedPlaceId");

  useEffect(() => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    HTTPClient.get(isAdmin(user) ? "/admin/places" : "/places")
      .then(({ data }: { data: Place[] }) => {
        // console.log(`Found ${data?.length ?? 0} places.`);
        const uniquePlaces = data.filter(
          (place, index) => data.findIndex((p) => p.id === place.id) === index
        );
        setPlaces(uniquePlaces);
        setIsLoading(false);
        if (data.length > 0) {
          const storedPlace = storedPlaceId ? data.find((p) => p.id === storedPlaceId) : null;
          if (storedPlaceId && !storedPlace) console.log(`Cannot find ${storedPlaceId} in places.`);

          const placeToSelect = storedPlace ?? data[0];
          setSelectedPlace(placeToSelect);
        } else if (searchParams.get("brand")) {
          createPlace({ label: searchParams.get("brand") as string })
            .then((place) => {
              // console.log("Created place : ", place);
              changePlace(place.id);
            })
            .catch(defaultError);
        }
      })
      .catch(() => {
        setIsLoading(false);
        setPlaces([]);
        localStorage.removeItem("tgc-selectedPlaceId");
      });
  }, [user]);

  useEffect(() => {
    localStorage.setItem("tgc-selectedPlaceId", selectedPlace?.id);
  }, [selectedPlace])

  const fetchPlaces = () =>
    new Promise<void>((resolve, reject) => {
      HTTPClient.get(isAdmin(user) ? "/admin/places" : "/places")
        .then(({ data }: { data: Place[] }) => {
          // console.log(`Found ${data?.length ?? 0} places.`);
          // remove duplicates id
          const uniquePlaces = data.filter(
            (place, index) => data.findIndex((p) => p.id === place.id) === index
          );
          setPlaces(uniquePlaces);
          resolve();
        })
        .catch(() => {
          reject();
          setIsLoading(false);
          setPlaces([]);
          localStorage.removeItem("tgc-selectedPlaceId");
        });
    });

  const changePlace = (placeId: string) => {
    const place = places.find((p) => p.id === placeId);
    // console.log("Place changed to: ", placeId);
    // console.log("Place: ", place);
    // console.log("Places: ", places);
    if (!place) {
      // console.log("Fetching Place: ", place);
      fetchPlaces().then(() => {
        // console.log("Places after fetch: ", places);
        if (places.find((p) => p.id === placeId))
          setSelectedPlace(places.find((p) => p.id === placeId));
      });
      return;
    }
    setSelectedPlace(place);
    localStorage.setItem("tgc-selectedPlaceId", placeId);
  };

  const createPlace = async (place: Omit<Place, "id">) => {
    const { data: newPlace } = await HTTPClient.post<Place>("/place", place);
    setPlaces([...places, newPlace]);
    setSelectedPlace(newPlace);
    //console.log("New place : ", newPlace);
    return newPlace;
  };

  const contextValue: PlaceProviderContextProps = {
    isLoading,
    selectedPlace,
    places,
    changePlace,
    createPlace,
    refreshPlaces: fetchPlaces,
  };

  return <PlaceContext.Provider value={contextValue}>{children}</PlaceContext.Provider>;
};

const usePlace = () => {
  const context = useContext(PlaceContext);
  if (!context) {
    throw new Error("usePlace must be used within a PlaceProvider");
  }
  return context;
};

export { PlaceProvider, usePlace };
